import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import {
  Megaphone, Building2, User, Mail, Phone, Globe,
  MessageSquare, DollarSign, Clock, RefreshCw,
  CheckCircle, XCircle, Loader2, Trash2, ChevronDown, ChevronUp
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const AdvertisingInquiriesManager = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchInquiries();
  }, [statusFilter]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = statusFilter 
        ? `${API_BASE}/api/admin/advertising/inquiries?status=${statusFilter}`
        : `${API_BASE}/api/admin/advertising/inquiries`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data.inquiries || []);
        setStatusCounts(data.status_counts || {});
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load advertising inquiries',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateInquiry = async (inquiryId, updateData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/advertising/inquiries/${inquiryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Inquiry updated successfully'
        });
        fetchInquiries();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update inquiry',
        variant: 'destructive'
      });
    }
  };

  const deleteInquiry = async () => {
    if (!inquiryToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/advertising/inquiries/${inquiryToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: 'Deleted',
          description: 'Inquiry deleted successfully'
        });
        setDeleteDialogOpen(false);
        setInquiryToDelete(null);
        fetchInquiries();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete inquiry',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      viewed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      contacted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      in_progress: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    const labels = {
      new: 'New',
      viewed: 'Viewed',
      contacted: 'Contacted',
      in_progress: 'In Progress',
      closed: 'Closed'
    };
    return <Badge className={styles[status] || styles.new}>{labels[status] || status}</Badge>;
  };

  const getAdTypeBadge = (adType) => {
    const labels = {
      banner: 'Banner Ad',
      sponsored: 'Sponsored Content',
      featured: 'Featured Listing',
      other: 'Other'
    };
    return <Badge variant="outline">{labels[adType] || adType}</Badge>;
  };

  const getBudgetLabel = (budget) => {
    const labels = {
      under_500: 'Under $500/mo',
      '500_1000': '$500-$1K/mo',
      '1000_2500': '$1K-$2.5K/mo',
      '2500_5000': '$2.5K-$5K/mo',
      over_5000: 'Over $5K/mo'
    };
    return labels[budget] || budget || 'Not specified';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
            <Megaphone className="w-6 h-6 text-red-600" />
            Advertising Inquiries
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage advertising requests from potential partners
          </p>
        </div>
        <Button onClick={fetchInquiries} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: 'new', label: 'New', color: 'blue', icon: '🆕' },
          { key: 'viewed', label: 'Viewed', color: 'green', icon: '👁️' },
          { key: 'contacted', label: 'Contacted', color: 'yellow', icon: '📞' },
          { key: 'in_progress', label: 'In Progress', color: 'purple', icon: '⏳' },
          { key: 'closed', label: 'Closed', color: 'gray', icon: '✅' }
        ].map(({ key, label, color, icon }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={`p-4 rounded-lg border-2 transition-all ${
              statusFilter === key 
                ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/30` 
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            <div className="text-2xl mb-1">{icon}</div>
            <div className="text-2xl font-bold dark:text-white">{statusCounts[key] || 0}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
          </button>
        ))}
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : inquiries.length === 0 ? (
        <Card className="dark:bg-gray-800">
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No advertising inquiries found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <Card key={inquiry.id} className="dark:bg-gray-800 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setExpandedId(expandedId === inquiry.id ? null : inquiry.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <CardTitle className="text-lg dark:text-white">
                        {inquiry.company_name}
                      </CardTitle>
                      {getStatusBadge(inquiry.status)}
                      {getAdTypeBadge(inquiry.ad_type)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" /> {inquiry.contact_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {inquiry.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {formatDate(inquiry.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {expandedId === inquiry.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {expandedId === inquiry.id && (
                <CardContent className="border-t dark:border-gray-700 pt-4 space-y-4">
                  {/* Contact Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {inquiry.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="dark:text-gray-300">{inquiry.phone}</span>
                      </div>
                    )}
                    {inquiry.website && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <a href={inquiry.website} target="_blank" rel="noopener noreferrer" 
                           className="text-blue-600 hover:underline dark:text-blue-400">
                          {inquiry.website}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="dark:text-gray-300">Budget: {getBudgetLabel(inquiry.budget_range)}</span>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      <span className="font-medium dark:text-white">Message</span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {inquiry.message}
                    </p>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-white">
                      Admin Notes
                    </label>
                    <Textarea
                      value={editingNotes[inquiry.id] ?? inquiry.admin_notes}
                      onChange={(e) => setEditingNotes({...editingNotes, [inquiry.id]: e.target.value})}
                      placeholder="Add notes about this inquiry..."
                      rows={3}
                      className="dark:bg-gray-700"
                    />
                    {editingNotes[inquiry.id] !== undefined && editingNotes[inquiry.id] !== inquiry.admin_notes && (
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          updateInquiry(inquiry.id, { admin_notes: editingNotes[inquiry.id] });
                          setEditingNotes({...editingNotes, [inquiry.id]: undefined});
                        }}
                      >
                        Save Notes
                      </Button>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t dark:border-gray-700">
                    <select
                      value={inquiry.status}
                      onChange={(e) => updateInquiry(inquiry.id, { status: e.target.value })}
                      className="px-3 py-2 border dark:border-gray-700 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="new">New</option>
                      <option value="viewed">Viewed</option>
                      <option value="contacted">Contacted</option>
                      <option value="in_progress">In Progress</option>
                      <option value="closed">Closed</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`mailto:${inquiry.email}?subject=RE: Advertising Inquiry - TrainerList`)}
                    >
                      <Mail className="w-4 h-4 mr-1" /> Reply via Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInquiryToDelete(inquiry.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reklam Talebini Sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu reklam talebini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInquiryToDelete(null)}>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={deleteInquiry} className="bg-red-600 hover:bg-red-700">
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdvertisingInquiriesManager;
